/**
 * client/src/pages/Rooms.tsx — *"Reading together"*: the rooms you are in, and how you get back into
 * one.
 *
 * design.md §7.6, *Finding the room*: `GET /api/v1/rooms/mine` (rows where you are a member and
 * `endedAt IS NULL`), a **"Reading together" row on the shelf**, and `/r/:code` as a shareable URL
 * that joins on open. Three ways in, because a Tuesday-night room is a thing you return to after a
 * week and none of the three is reliably the one somebody remembers.
 *
 * ── Why the membership list is the product and not the lobby ────────────────
 * movies has a public theater lobby and it is the right shape *there*: a film night is an event you
 * discover. A reading room is *the Bible with Dad on Tuesdays* — it is not discovered, it is
 * **returned to**, and §7.6 makes that concrete by defaulting `persistent` to 1 and never GC-ing a
 * persistent room. So this screen is the list of rooms you are already in, and `listing` (`shown` /
 * `masked` / `hidden`) is a per-room setting that a public lobby would consume, not the organising
 * idea of the screen.
 *
 * ── The row is exported, and that is deliberate ─────────────────────────────
 * `<ReadingTogetherRow>` is the shelf's strip and this file's own list, one component. `App.tsx`'s
 * `BookCover` comment is the scar this avoids: books.karti.ai kept three independent copies of one
 * page grid, and when two were deleted the third kept its *names* and lost its *geometry*. Sharing
 * the names is not sharing the layout — so the layout is shared.
 */

import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AppFrame, BookCover, timeAgo } from "../App.tsx";
import { api, ApiError } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { asMyRooms, roomPath, type MyRoom } from "../sync/room.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * Loading
 * ══════════════════════════════════════════════════════════════════════════ */

export interface MyRoomsState {
  rooms: MyRoom[];
  loading: boolean;
  /** Null when `/rooms/mine` is simply not built yet — see `isUnbuilt` below. */
  error: string | null;
  unbuilt: boolean;
  reload(): void;
}

/**
 * A 404 on `/rooms/mine` means *the route does not exist*, not *you have no rooms*.
 *
 * The distinction is worth a function because the two need opposite screens: "no rooms yet" is an
 * invitation, and "this isn't built" is an honest admission. Rendering the invitation over a missing
 * route is exactly the failure AGENTS.md's *only claim what is built* rule is about, arriving from
 * the direction nobody checks — nothing looks broken while a screen under-promises, and nothing
 * looks broken while it silently over-promises an empty list either.
 *
 * `not-json` is the sibling case: `lib/api.ts` raises it when the SPA fallback served `index.html` to
 * a fetch, which is what a missing server route looks like through a proxy.
 */
export function isUnbuilt(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.reason === "not-json");
}

export function useMyRooms(): MyRoomsState {
  const [rooms, setRooms] = useState<MyRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unbuilt, setUnbuilt] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      try {
        const body = await api.get<unknown>("/rooms/mine");
        if (!live) return;
        setRooms(asMyRooms(body));
        setError(null);
        setUnbuilt(false);
      } catch (err) {
        if (!live) return;
        setRooms([]);
        if (isUnbuilt(err)) {
          setUnbuilt(true);
          setError(null);
        } else {
          setUnbuilt(false);
          setError(err instanceof Error ? err.message : "Couldn't load your rooms.");
        }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [nonce]);

  return { rooms, loading, error, unbuilt, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

/* ══════════════════════════════════════════════════════════════════════════
 * The row
 * ══════════════════════════════════════════════════════════════════════════ */

const strip: CSSProperties = {
  display: "flex",
  gap: "var(--space-3)",
  overflowX: "auto",
  padding: "var(--space-1) 0 var(--space-3)",
  scrollSnapType: "x proximity",
};

const card: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "3rem 1fr",
  gap: "var(--space-3)",
  alignItems: "center",
  flex: "0 0 auto",
  width: "min(20rem, 78vw)",
  padding: "var(--space-3)",
  border: "1px solid var(--rule)",
  borderRadius: "var(--radius-lg)",
  background: "var(--sheet)",
  color: "inherit",
  textDecoration: "none",
  scrollSnapAlign: "start",
};

export interface ReadingTogetherRowProps {
  rooms: readonly MyRoom[];
  /** The shelf renders a heading of its own; the rooms page does not need a second one. */
  heading?: boolean;
}

/**
 * The strip. Nothing when there is nothing — a "Reading together" heading over an empty rail on the
 * shelf of somebody who has never opened a room is furniture that never goes away.
 */
export function ReadingTogetherRow({ rooms, heading = true }: ReadingTogetherRowProps): ReactElement | null {
  if (rooms.length === 0) return null;
  return (
    <section aria-label="Reading together">
      {heading ? <h2 className="ab-h2">Reading together</h2> : null}
      <div style={strip}>
        {rooms.map((r) => (
          <Link key={r.code} to={roomPath(r.code)} style={card}>
            <span style={{ width: "3rem" }}>
              <BookCover bookId={r.bookId} title={r.bookTitle} coverSha={r.coverSha} />
            </span>
            <span style={{ display: "grid", gap: "0.125rem", minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: "var(--ink)" }}>{r.name}</span>
              <span
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--ink-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.bookTitle}
              </span>
              {/* §7.6's resume line. A **chapter**, never a page number — §5.1: "Page numbers are
                  never persisted", and a page number here would be this device's page number for
                  somebody else's font size. */}
              <span style={{ fontSize: "0.75rem", color: "var(--ink-faint)" }}>
                {r.roomChapterTitle
                  ? `Stopped at ${r.roomChapterTitle}`
                  : r.roomChapterOrd !== null
                    ? `Stopped in chapter ${r.roomChapterOrd}`
                    : "Not started"}
                {r.lastActivityAt > 0 ? ` · ${timeAgo(r.lastActivityAt)}` : ""}
              </span>
              <span style={{ fontSize: "0.75rem", color: "var(--ink-faint)" }}>
                {r.connectedCount > 0
                  ? `${r.connectedCount} here now`
                  : `${r.memberCount} ${r.memberCount === 1 ? "member" : "members"}`}
                {" · "}
                {r.mode === "read" ? "reading" : "listening"}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The screen
 * ══════════════════════════════════════════════════════════════════════════ */

/** `/rooms`. The full list, plus the one field that gets you into a room somebody read out to you. */
export function Rooms(): ReactElement {
  const { signedIn } = useAuth();
  const navigate = useNavigate();
  const mine = useMyRooms();
  const [code, setCode] = useState("");

  return (
    <AppFrame>
      <div className="ab-shelf-head">
        <h1 className="ab-h1">Reading together</h1>
      </div>

      {/* A code typed in, because that is how a room actually arrives most of the time: read out
          over the phone, or in a message with the link stripped. `/r/:code` joins on open (§7.6) and
          this is the same door with a keyboard in front of it. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const c = code.trim();
          if (c) navigate(roomPath(c));
        }}
        style={{ display: "flex", gap: "var(--space-2)", margin: "var(--space-3) 0 var(--space-5)" }}
      >
        <input
          className="ab-search"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Room code"
          aria-label="Room code"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={12}
        />
        <button type="submit" className="ab-btn ab-btn-primary" disabled={code.trim().length === 0}>
          Join
        </button>
      </form>

      {mine.loading ? (
        <div className="ab-blank" aria-hidden />
      ) : mine.unbuilt ? (
        // AGENTS.md's rule, and its expensive second half: a "not built yet" list goes stale in the
        // direction nobody checks. The phase number is in the copy so a grep for `P5` finds it.
        <p className="ab-phase-note">
          <span className="ab-chip ab-chip-soon">P5</span>
          <span>
            The rooms service isn&rsquo;t answering yet. A room you already have a link to still opens
            — this list is what needs <code>GET /api/v1/rooms/mine</code>.
          </span>
        </p>
      ) : mine.error ? (
        <div className="ab-empty">
          <h2>Couldn&rsquo;t load your rooms.</h2>
          <p>{mine.error}</p>
          <button type="button" className="ab-btn" onClick={mine.reload}>
            Try again
          </button>
        </div>
      ) : mine.rooms.length === 0 ? (
        <div className="ab-empty">
          <h2>No rooms yet.</h2>
          <p>
            Open a book and start a room from the reader, then send the link. Whoever opens it joins —{" "}
            {signedIn ? "with or without an account." : "no account needed."}
          </p>
          <Link to="/" className="ab-btn ab-btn-primary">
            Go to the shelf
          </Link>
        </div>
      ) : (
        <ReadingTogetherRow rooms={mine.rooms} heading={false} />
      )}
    </AppFrame>
  );
}

export default Rooms;
